import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ChevronLeft, Loader2, Crown, Trophy, Share2 } from 'lucide-react';
import { shareRecordImage } from '../shareRecord';
import { MuscleGroupChip } from './ExerciseLibraryUI';

const fmtDate = (iso, en) => {
  try {
    return new Date(iso).toLocaleDateString(en ? 'en-GB' : 'fr-CH', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

/** Salle des Records : palmarès chronologique de tous les records personnels (1RM estimé) battus par le client. */
export const RecordsHall = ({ en, onBack, authFetch }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [data, setData] = useState(null);
  const [sharingIdx, setSharingIdx] = useState(null);
  const [selectedMuscle, setSelectedMuscle] = useState('');

  useEffect(() => {
    authFetch('/api/tracking/programs-pro/insights/records')
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ locked: true, records: [] }));
  }, [authFetch]);

  const handleShare = async (r, i) => {
    setSharingIdx(i);
    try {
      await shareRecordImage({ exerciseName: r.name, oldWeight: r.old_weight, newWeight: r.new_weight, pctChange: r.pct_change, en });
    } catch (e) { if (e?.name !== 'AbortError') toast.error(L('Impossible de générer l\u2019image', 'Could not generate the image')); }
    setSharingIdx(null);
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const allRecords = data.records || [];
  const muscleGroupsPresent = [...new Set(allRecords.map((r) => r.muscle_group).filter(Boolean))];
  const records = selectedMuscle ? allRecords.filter((r) => r.muscle_group === selectedMuscle) : allRecords;

  return (
    <div className="space-y-5" data-testid="records-hall-view">
      {onBack && (
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="back-to-hub-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
      )}
      <h2 className="text-xl font-black text-white flex items-center gap-2">
        <Trophy className="w-5 h-5 text-yellow-400" /> {L('Salle des Records', 'Hall of Records')}
      </h2>
      <p className="text-sm text-zinc-500">
        {L('Chaque record personnel (1RM estimé) battu pendant vos séances, dans l\u2019ordre chronologique.', 'Every personal record (estimated 1RM) beaten during your sessions, in chronological order.')}
      </p>

      {allRecords.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-10 text-center text-zinc-500 text-sm" data-testid="records-hall-empty">
          {L('Aucun record battu pour le moment. Continuez à vous entraîner, le premier arrive vite !', 'No record beaten yet. Keep training, your first one is coming soon!')}
        </div>
      ) : (
        <>
          <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="records-hall-muscle-filters">
            <button onClick={() => setSelectedMuscle('')}
              className={`flex flex-col items-center gap-1 px-2.5 py-2 rounded-2xl text-[9px] font-bold whitespace-nowrap border shrink-0 ${!selectedMuscle ? 'bg-gradient-to-br from-primary to-pink-500 text-white border-transparent shadow-lg scale-105' : 'bg-zinc-800/60 text-zinc-300 border-white/10'}`}
              data-testid="records-hall-filter-all">
              <Trophy className="w-4 h-4" />
              <span className="leading-none">{L('Tous', 'All')}</span>
            </button>
            {muscleGroupsPresent.map((g) => (
              <MuscleGroupChip key={g} group={g} active={selectedMuscle === g} onClick={() => setSelectedMuscle(selectedMuscle === g ? '' : g)} testid={`records-hall-filter-${g}`} />
            ))}
          </div>

          {records.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-10 text-center text-zinc-500 text-sm" data-testid="records-hall-filtered-empty">
              {L('Aucun record pour ce groupe musculaire.', 'No record for this muscle group.')}
            </div>
          ) : (
        <div className="relative pl-6 space-y-4 before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-0.5 before:bg-gradient-to-b before:from-yellow-500/60 before:via-primary/40 before:to-transparent">
          {records.map((r, i) => (
            <motion.div
              key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(i * 0.04, 0.5) }}
              className="relative rounded-2xl bg-zinc-900/60 border border-yellow-500/15 p-4"
              data-testid={`record-item-${i}`}
            >
              <span className="absolute -left-6 top-4 w-4 h-4 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-[0_0_10px_rgba(251,191,36,0.6)]">
                <Crown className="w-2.5 h-2.5 text-black" />
              </span>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-white font-bold">{r.name}</p>
                  <p className="text-[11px] text-zinc-500">{fmtDate(r.performed_at, en)} · {r.muscle_group}</p>
                </div>
                <span className="text-lg font-black text-yellow-400">+{r.pct_change}%</span>
              </div>
              <p className="text-sm text-zinc-400 mt-2">
                {L('1RM estimée', 'Estimated 1RM')}: <span className="text-zinc-300 font-semibold">{r.old_weight}kg</span> → <span className="text-yellow-400 font-bold">{r.new_weight}kg</span>
              </p>
              <button onClick={() => handleShare(r, i)} disabled={sharingIdx === i}
                className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-400 hover:text-white disabled:opacity-50"
                data-testid={`share-record-hall-btn-${i}`}>
                {sharingIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />} {L('Partager', 'Share')}
              </button>
            </motion.div>
          ))}
        </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecordsHall;
